import { reactive, onMounted, watch } from "vue";
import { getKZValue } from "@/DataHelper";
import { AufgabeChartParams, getAufgabeDefRow, openZusatzInfoDialog } from "../ChartTypesAndFunctions";
import { getSiteData, onShowSite } from "@/NavigationHelper";
import { Globals } from "@/Globals";
import { begriffBezeichnung, globalsStt } from "@/local";
import AUApi from "@/api";

export interface ChartBaseProperties {
    params: AufgabeChartParams;
    QvAICs?: number[];
    QuickViewMode?: boolean;
    chartOrientation?: string;
}
export interface ChartBaseParameters {
    formular?: ALLFrame | ALLAufgabe; // STMM: wird mal das und mal das andre zugewiesen... sollte man das nicht zusammenfürhen ?
    lastQueryResponse?: QueryResponse;
    aufgabenDefRow?: QueryDataRow;
    query: string;
    refreshing: boolean;
    aic?: number;
    zeitbereich?: APIZeitbereich;
    size?: any;
    title?: string;
    tooltips?: boolean;
    icon?: string;
    bewegungsdaten: any;
    orientation?: string;
    zuordnungen?: any[];
    aufgabenDefinition?: any;
    zusatzInfo?: any;
    zusatzFrame?: any;
    queryData?: any;
    aicStamm?: number;
    abwZeitbereich?: string;
    aufgabe?: any;
}
export interface ChartBaseUiData {
    showLoading: boolean;
    showZeitbereich?: boolean;
    titleZeitbereich?: string;
    vector: number[];
    // query: string; // aus params verwenden
    // aic: number;   // aus params verwenden
    size: number;
    title: string;
    tooltips: boolean;
    tooltip?: string[];
    icon: string;
    orientation: string;
    zuordnungen: any[];
    aufgabenDefinition: QueryResponse;
    zusatzInfo: any;
    zusatzFrame: any;
}

export function initChartBase<UIData extends ChartBaseUiData, Params extends ChartBaseParameters>(props: ChartBaseProperties, defaults: Partial<UIData>, paramDefaults: Partial<ChartBaseParameters>) 
    
{
    const uiData: ChartBaseUiData = {
        showLoading: defaults.showLoading ?? false,
        vector: [],
        size: props.params.size ?? 300, // default besprechen
        title: props.params.title ?? "",
        tooltips: props.params.tooltips ?? true,
        icon: props.params.icon ?? "poll",
        orientation: props.params.orientation ?? "horizontal",
        zuordnungen: props.params.zuordnungen ?? [],
        aufgabenDefinition: props.params.aufgabenDefinition!,
        zusatzInfo: props.params.zusatzInfo,
        zusatzFrame: props.params.zusatzFrame,
    };

    const params: ChartBaseParameters = {
        aic: props.params.aic ?? 0,
        query: props.params.query,
        zeitbereich: props.params.zeitbereich ?? AUApi.Current.zeitbereich,
        queryData: props.params.queryData,
        refreshing: false,
        bewegungsdaten: props.params.bewegungsdaten,
    }

    Object.assign(uiData, defaults);
    Object.assign(params, paramDefaults);
    return {
        ui: (<UIData>uiData),
        params: (<Params>params)
    };
}

export function dynamicSort(property: any) {
    let sortOrder = 1;
    if (property[0] === "-") {
        sortOrder = -1;
        property = property.substr(1);
    }
    return (a: any, b: any) => {
        const result =
            a[property].localeCompare < b[property].localeCompare ? -1
                : a[property].localeCompare > b[property].localeCompare ? 1 : 0;
        return result * sortOrder;
    };
}

export function getKZAufgabeDefiniton<T>(uiData: ChartBaseUiData, props: ChartBaseProperties, params: ChartBaseParameters, kz: string, defaultVal?: T) {
    if (!props.params?.aufgabenDefinition) {
        return defaultVal;
    }
    if (!params.aufgabenDefRow && props.params.aicStamm) {
        params.aufgabenDefRow = getAufgabeDefRow(props.params.aicStamm, props.params?.aufgabenDefinition);
    }
    if (!params.aufgabenDefRow) {
        return defaultVal;
    }
    return getKZValue(params.aufgabenDefRow, kz, props.params.aufgabenDefinition?.columns) ?? defaultVal;
}

export function setzteVector(uiData: ChartBaseUiData, vec: number[], emit: (event: string, ...args: any[]) => void) {
    uiData.vector = vec;
    emit("vectorChanged", uiData.vector);
}

export function showDialogChart(uiData: ChartBaseUiData | DataPageUIData, props: ChartBaseProperties, zeitbereich: APIZeitbereich, kpi: any, aic?: number) {
    let item = 'zusatzInfo' in uiData ? uiData.zusatzInfo : undefined;
    if (aic) {
        item = kpi;
    }
    if (item && item.gruppe === "Frame") {
        const site = item.alias ?? item.kennung;
        let auswahlQuery = "";
        let neuDialog = "";
        let stammQuery = "";
        let sysAbfrage = "";
        if (site) {
            if (item.stammQuery) {
                stammQuery = item.stammQuery;
                auswahlQuery = item.auswahlQuery ?? "";
                neuDialog = item.neuDialog ?? "";
            }
        }
        if (item.kennung === "EditStamm") {
            const stt = kpi.aicStamm;
            const noFilter = true;
            const begr = globalsStt(Globals.Begriff, stt, "", noFilter); // holt system Abfrage
            if (begr) {
                sysAbfrage = begr.kennung;
            }
        }
        onShowSite({
            title: item.name ?? item.bezeichnung,
            site,
            showZeitraumHeader: false,
            data: {
                zeitbereich: AUApi.Current.zeitbereich,
                item,
                aic: aic ?? kpi.aic,
                showBack: true,
                toggleUebergabe: item.toggleSight,
                name: item.name ?? item.bezeichnung,
                titel: item.name ?? item.bezeichnung,
                gotoToday: false,
                query: sysAbfrage,
                stammQuery,
                auswahlQuery,
                neuDialog,
            },
        });
    } else if (item && item.gruppe === "Hauptschirm") {
        const siteHS = "BaseHsTreeView";
        const hsAicSelected = item.aicHS;
        onShowSite({
            title: item.name ?? item.bezeichnung,
            site: siteHS,
            data: {
                zeitbereich: AUApi.Current.zeitbereich,
                item,
                aic: kpi.aic,
                showBack: true,
                toggleUebergabe: item.toggleSight,
                name: item.name ?? item.bezeichnung,
                titel: item.name ?? item.bezeichnung,
                gotoToday: false,
                hsAicSelected,
                showZeitraum: false,
            },
        });
    } else if (item && item.gruppe === "Aufgabe") {
        openZusatzInfoDialog(
            item,
            undefined,
            kpi,
            'aufgabenDefinition' in uiData ? uiData.aufgabenDefinition : undefined,
            zeitbereich,
            props.params.aicStamm
        ).catch(e => console.error(e));
    } else {
        console.log("no zusatzinfo - no dialog..");
    }
}


export async function refreshChartData(uiData: ChartBaseUiData, props: ChartBaseProperties, params: ChartBaseParameters
) {
    if (props.params?.aufgabe) {
        params.formular = props.params.aufgabe;
    }
    uiData.showLoading = true;
    if (params.refreshing) { return; }
    params.refreshing = true;
    let currentZeitbereich = AUApi.Current.zeitbereich;
    const zeitbereichVon = AUApi.Current.zeitbereich.von;
    const zeitbereichBis = AUApi.Current.zeitbereich.bis;
    let bereich = "Monat";
    let vonDate = new Date(zeitbereichVon);
    let bisDate = new Date(new Date(zeitbereichBis).lastInMonth());
    currentZeitbereich = {
        bereich,
        von: vonDate,
        bis: bisDate,
    };
    if (props.params.abwZeitbereich) {
        if (props.params.abwZeitbereich === "Woche") {
            bereich = props.params.abwZeitbereich;
            const heute = new Date();
            vonDate = new Date(heute.firstOfWeek());
            bisDate = vonDate.addDays(+6);
            uiData.showZeitbereich = true;
            const week = vonDate.getWeekNumber();
            uiData.titleZeitbereich = "KW: " + week.toFixed(0) + " ";
        } else if (props.params.abwZeitbereich === "Jahr") {
            bereich = props.params.abwZeitbereich;
            vonDate = new Date(vonDate.getFullYear(), 1, 0);
            vonDate = vonDate.firstOfMonth();
            bisDate = vonDate.addMonths(+11, true);
            bisDate = bisDate.lastInMonth();
            uiData.showZeitbereich = true;
            const vonYear = vonDate.getFullYear();
            const textJahr = begriffBezeichnung("Jahr");
            uiData.titleZeitbereich = textJahr + ": " + vonYear.toFixed(0) + " ";
        }
    }

    currentZeitbereich = {
        bereich,
        von: vonDate,
        bis: bisDate,
    };
    try {
        if (params.query) {
            const setztVector = "";
            let formAic = 0;
            if (params.formular) {
                formAic = params.formular.aic;
            }
            const r = await AUApi.Current.getQuery(
                params.query,
                undefined,
                params.aic ?? null,
                currentZeitbereich ?? null,
                0,
                false,
                params.bewegungsdaten,
                setztVector,
                formAic,
            );
            // muss es in params setzen - damit ich im CChart - dann darauf zugreifen kann
            params.lastQueryResponse = params.queryData = r.data
            return params.queryData;
        } else if (props.params.queryData) {
            params.lastQueryResponse = params.queryData = props.params.queryData;
            return params.queryData;
        } else {
            const siteData = getSiteData();
            if (siteData) {
                if (siteData.queryData) {
             
                    return params.lastQueryResponse = params.queryData = siteData.queryData;
                }
            }
        }
    } catch (e) {
        console.error(e);
        return undefined;
    } finally {
        params.refreshing = false;
    }
}
